import React, { memo, useCallback, useMemo } from 'react'

import { Divider, FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, oneOf } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setPurchaseFilter,
  togglePersonalFilter,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { PURCHASE_FILTER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  getGeneralFiltersConfig,
  getHidePurchasedFiltersConfig,
  getStatusFiltersConfig,
  SECTION,
} from './constants'

const PURCHASE_FILTER_VALUES = Object.values(PURCHASE_FILTER)

// hideGeneralLabel drops only the General group's title when an external title already names the
// section (mobile collapsible header). The Status label stays as a sub-heading.
// section splits the groups for the More filters columns, where Status and General (Other) sit in
// separate sections (Figma 3183:36396).
function AdvancedFiltersContent({ hideGeneralLabel = false, section = SECTION.ALL }) {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters ?? EMPTY_ARRAY)
  const personalFilters = useSearchPageStore(state => state.personalFilters ?? EMPTY_ARRAY)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  // Personal catalog filters (wishlist, purchased) are backed by ItemsSearch filters that no-op
  // without current_user, so a signed-out viewer would see an "active" filter over unfiltered
  // results. Hidden until pageConfig confirms a logged-in user (client-side; SSR is user-blind).
  const pageConfig = usePageConfig()
  const isLoggedIn = !!pageConfig?.currentUserId
  const trackFilterSelect = useTrackFilterSelect()

  const handleGeneralFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.ADVANCED, value)
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [trackFilterSelect])

  const handleStatusFilterChange = useCallback((_, value, isOn) => {
    if (PURCHASE_FILTER_VALUES.includes(value)) {
      if (isOn) trackFilterSelect(FILTER_NAME.PURCHASES, value)
      setPurchaseFilter(isOn ? value : undefined)
      return
    }

    if (isOn) trackFilterSelect(FILTER_NAME.PERSONAL, value)
    togglePersonalFilter(value, isOn)
  }, [trackFilterSelect])

  const selectedStatusFilters = useMemo(() => [
    ...(PURCHASE_FILTER_VALUES.includes(purchaseFilter) ? [purchaseFilter] : []),
    ...personalFilters,
  ], [personalFilters, purchaseFilter])

  const generalConfig = useMemo(() => {
    const config = getGeneralFiltersConfig(translate)
    return hideGeneralLabel ? [{ ...config[0], label: '' }] : config
  }, [hideGeneralLabel, translate])

  const statusConfig = useMemo(() => getStatusFiltersConfig(translate), [translate])
  const hidePurchasedConfig = useMemo(() => getHidePurchasedFiltersConfig(translate), [translate])

  const showGeneral = section !== SECTION.STATUS
  const showStatus = section !== SECTION.GENERAL && isLoggedIn

  return (
    <>
      {showGeneral && (
        <FiltersSidebar
          isCollapsible={false}
          filters={generalConfig}
          selectedFilters={advancedFilters}
          onFiltersChange={handleGeneralFilterChange}
          isControlled
          useNewCheckboxes
          hideLastDivider={!showStatus}
          hideShowMore
          baseFormWrapperClassName="filter-wrapper__content"
        />
      )}
      {showStatus && (
        <>
          <FiltersSidebar
            isCollapsible={false}
            filters={statusConfig}
            selectedFilters={selectedStatusFilters}
            onFiltersChange={handleStatusFilterChange}
            isControlled
            useNewCheckboxes
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
          />
          <Divider />
          <FiltersSidebar
            isCollapsible={false}
            filters={hidePurchasedConfig}
            selectedFilters={selectedStatusFilters}
            onFiltersChange={handleStatusFilterChange}
            isControlled
            useNewCheckboxes
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
          />
        </>
      )}
    </>
  )
}

AdvancedFiltersContent.propTypes = {
  hideGeneralLabel: bool,
  section: oneOf(Object.values(SECTION)),
}

export default memo(AdvancedFiltersContent)
